import React from 'react';
import { view } from '@risingstack/react-easy-state';
import { ConfirmationModalFn } from 'framework/components/ui/ConfirmationModal/ConfirmationModal';
import { FileManagerAddFolder } from './FileManagerComponents';
import { FileManagerMoveFileModal } from './FileManagerMoveFileModal';
import { useFileManagerContextInternal, FileManagerContext } from './FileManagerContext';
import { FILEMANAGER_DELETE_CONFIRM_MODAL } from './constants';
import { FileFolder, FileFolderType } from './types';
import { Table } from '../../../components/ui';
import { i18n } from 'i18n';

type FileManagerContextProvider = {
  confirmationModalName?: string;
  children: React.ReactNode;
};

export const generateWarning = (deleteMode: FileFolderType, isSingleDelete: boolean): string => {
  if (deleteMode === FileFolderType.DIRECTORY) return 'modules.contentmanagement.confirmDeleteFolder';

  return isSingleDelete
      ? 'modules.contentmanagement.dam.confirm_delete_file'
      : 'modules.contentmanagement.dam.confirm_delete_multiple_files';
};

// If previewableFile is selected, then user is performing a single delete
export const checkSingleDelete = (
  selectedFiles: FileFolder[],
  previewableFile: FileFolder,
  isFileSelected: (file: FileFolder) => boolean,
): boolean =>
  selectedFiles?.length === 0 || (previewableFile && !isFileSelected(previewableFile));

export const FilesTable = (selectedFiles: FileFolder[]) => <Table
    noBorder
    data={selectedFiles}
    columnMetadata={[
      i18n.t('modules.contentmanagement.columnFileName'),
      i18n.t('modules.contentmanagement.columnPath'),
    ]}
    cellRenderer={(_, file) => (
      <Table.Row>
        <Table.ColumnText caption={file.name} />
        <Table.ColumnText caption={file.reference} />
      </Table.Row>
    )}
  />;

export const FileManagerContextProvider = view(({
  confirmationModalName,
  children,
}: FileManagerContextProvider) => {
  const {
    forRoot,
    activeFolder,
    rootFolderModalOpen,
    moveFileModalOpen,
    previewableFile,
    store,
    deleteMode,
    onDeleteFunc,
    onFileMoved,
    onSubFolderCreated,
    onFolderCreated,
    ...rest
  } = useFileManagerContextInternal(confirmationModalName);

  const isSingleDelete: boolean = checkSingleDelete(rest.selectedFiles, previewableFile, rest.isFileSelected);

  return (
        <FileManagerContext.Provider value={{
          previewableFile,
          activeFolder,
          store,
          ...rest,
        }}>
          <ConfirmationModalFn
            name={confirmationModalName || FILEMANAGER_DELETE_CONFIRM_MODAL}
            warning={generateWarning(deleteMode, isSingleDelete)}
            onConfirmDelete={onDeleteFunc}
            {...deleteMode === FileFolderType.FILE && ({
              modalBodyComponent: () => FilesTable(isSingleDelete ? [previewableFile] : rest.selectedFiles)
            })}
          />
          {rootFolderModalOpen && (
          <FileManagerAddFolder
            parentFolder={forRoot ? activeFolder?.reference : undefined}
            onFolderCreated={forRoot ? onSubFolderCreated : onFolderCreated}
          />
          )}
          {moveFileModalOpen && (
            <FileManagerMoveFileModal
              movableFile={previewableFile}
              onFileMoved={onFileMoved}
            />
          )}
            {children}
        </FileManagerContext.Provider>
  );
});
